import { type IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { useEffect } from 'react'
import { useParams } from 'react-router'

import { type OnboardingPage } from '../routes'
import Calendly from '../../components/Calendly'
import { USERS } from '../../constants/users_types'
import { useQuotation } from '../context'
import {
  GetQuotationDocument,
  GetQuotationQuery,
  GetQuotationQueryVariables,
  UserTypeEnum,
} from '../../generated/graphql'
import { track } from '../../utils/analytics'
import usePartner from '../../utils/usePartner'
import { TypeCard } from '../components/TypeCard'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { useOnboardingWorkflow } from '../workflow'
import { TipsCard } from '../components/TipsCard'
import graphQLClient from '../../services/graphql/graphqlClient'

import { Button, Loader } from '@olino/design-system'

const categories: Array<{
  name: string
  as: 'h2' | 'h3'
  description?: string
  usersTypes: Array<{ title: string; icon: IconDefinition; value: UserTypeEnum; disabled: boolean }>
}> = [
  {
    name: 'Olino Autonomie',
    as: 'h2',
    description: "Choisissez le type d'utilisateur que vous êtes.",
    usersTypes: [
      {
        title: 'Collectivité',
        icon: USERS[UserTypeEnum.Collectivite].icon,
        value: UserTypeEnum.Collectivite,
        disabled: false,
      },
      {
        title: 'Professionnel',
        icon: USERS[UserTypeEnum.Professionnel].icon,
        value: UserTypeEnum.Professionnel,
        disabled: false,
      },
    ],
  },
]

const Main = () => {
  const { id } = useParams()
  const { updateQuotation, quotation } = useQuotation()
  const { partner, code: partnerCode } = usePartner()
  const { proceed } = useOnboardingWorkflow()

  const handleSelect = async (userType: UserTypeEnum) => {
    const { quotation: quotation } = await graphQLClient.request<
      GetQuotationQuery,
      GetQuotationQueryVariables
    >(GetQuotationDocument, { id: id ? id : '' })

    // if (
    //   !quotation?.details.contact ||
    //   (quotation?.details.contact && quotation?.details?.contact.userTypeName == userType)
    // ) {
    track({
      event: 'submitted_userType',
      quotation_id: id,
    })

    proceed(() =>
      updateQuotation({
        details: {
          contact: {
            userTypeName: userType,
          },
        },
      })
    )
    // }
  }

  useEffect(() => {
    track({
      event: 'visited_userType',
      quotation_id: id,
    })
  }, [id])

  if (partnerCode !== null && partner === null) return <Loader />

  return (
    <>
      <OnboardingTitle.H1>Quel utilisateur êtes vous&nbsp;?</OnboardingTitle.H1>

      <section className="space-y-8">
        {categories.map((category) => {
          const userTypeList = UserTypeEnum
            ? category.usersTypes.filter((userType) => UserTypeEnum)
            : category.usersTypes

          const isCategoryEmpty = partner && userTypeList.length === 0

          if (isCategoryEmpty) return null

          return (
            <div key={category.name}>
              <div className="grid gap-3 sm:grid-cols-2">
                {userTypeList.map((userType) => (
                  <TypeCard
                    key={userType.value}
                    onClick={() => handleSelect(userType.value)}
                    name={userType.title}
                    icon={userType.icon}
                    disabled={userType.disabled}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </section>

      <div className="mt-7 text-center">
        <span>Vous avez un autre besoin ? </span>
        <Calendly type="popup" reason="cant-find-product">
          {(open) => (
            <Button variant="link" onClick={open}>
              Cliquez ici pour en discuter avec un de nos experts.
            </Button>
          )}
        </Calendly>
      </div>
    </>
  )
}

const Tips = () => {
  return (
    <div className="space-y-3">
      <TipsCard
        title="Notre parcours en ligne"
        content={[
          'Nous vous proposons une expérience en ligne pour souscrire à vos produits de Pilliot CyberCriminalité Informatique. Vous pouvez à tout moment contacter un de nos experts pour poser vos questions.',
          'Si nous ne sommes pas en mesure de vous fournir un prix immédiatement, un de nos experts reviendra vers vous en moins de 48h.',
        ]}
      />
    </div>
  )
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
